import styles from './InspectionItemAsset.module.scss';
import noImage from '../../../assets/icons/image_blank.png';
import { Text } from '../../typography';
import { useTranslation } from 'react-i18next';
import {
  getTranslationKey,
  keys,
} from '../../../utilities/translator/translation_keys';
import { useNavigate } from 'react-router-dom';
import { getRoute, paths } from '../../../constants/strings';
import { useWorkspace } from '../../../providers/WorkspaceProvider';
import Icon from '../../Icon';
import { useState, useEffect } from 'react';
import { Form } from 'react-bootstrap';
import classNames from 'classnames';
import { vehicleString } from '../../../utilities';
import { useInspection } from '../../../hooks/offline-hooks/createInspectionhook';
import { useOnlineStatus } from '../../../hooks/offline-hooks/offline-misc';
import Button from '../../Button';

const Question = ({
  vehicle,
  setIsEditing,
  isEditing,
  updateAsset,
  readOnly,
  state,
  t,
  onChange,
  size,
  navigate,
  workspaceId,
  disableDirectWrite,
  online,
  setState,
}) => {
  const { id, hours, odometer } = state || {};

  return (
    <div className={styles.container}>
      <img
        src={vehicle?.imageUrl ?? noImage}
        onClick={() =>
          navigate(getRoute(workspaceId, paths.asset, vehicle?.id))
        }
        className={classNames([styles.image, styles[size]])}
        alt="vehicle"
      />
      <div className={styles.content}>
        <Text
          hover={online}
          noMargin
          color="accentPrimary"
          onClick={() => {
            if (!online) return;
            navigate(getRoute(workspaceId, paths.asset, vehicle?.id));
          }}
        >
          {vehicleString(vehicle)}
        </Text>
        <Text size="sm" color="secondary" noMargin>
          {`${
            t(getTranslationKey(vehicle?.type, 'assets')) || vehicle?.type
          } - ${
            t(getTranslationKey(vehicle?.subtype, 'assets')) || vehicle?.subtype
          }`}
        </Text>
        <div className={styles.details}>
          <div>
            {vehicle.showDistance ? (
              <div className={styles.row}>
                {isEditing ? (
                  <Form.Control
                    className={
                      state?.usageUpdateRequired
                        ? styles.highlight
                        : styles.form
                    }
                    type="number"
                    value={state?.odometer}
                    placeholder="Enter odometer"
                    min={0}
                    onChange={(e) =>
                      setState({ ...state, odometer: parseInt(e.target.value) })
                    }
                    onBlur={() => {
                      if (disableDirectWrite) {
                        onChange({ odometer });
                      } else {
                        updateAsset({ id, odometer });
                      }

                      setIsEditing(false);
                    }}
                  />
                ) : (
                  <Text noMargin weight="semiBold">
                    {`${vehicle?.odometer ?? 0}`}
                  </Text>
                )}
                <div className={styles.usageText}>
                  <Text noMargin color="secondary" weight="semiBold">
                    {vehicle?.isMetric ? 'km' : 'mi'}
                  </Text>
                  {vehicle?.usageUpdateRequired ? (
                    <Text noMargin color="red" weight="semiBold">
                      *
                    </Text>
                  ) : null}
                </div>
              </div>
            ) : null}
            {vehicle.showHours ? (
              <div className={styles.row}>
                {isEditing ? (
                  <Form.Control
                    type="number"
                    min={0}
                    className={
                      state?.usageUpdateRequired
                        ? styles.highlight
                        : styles.form
                    }
                    value={state?.hours}
                    placeholder="Enter hours"
                    onChange={(e) =>
                      setState({ ...state, hours: parseFloat(e.target.value) })
                    }
                    onBlur={() => {
                      if (disableDirectWrite) {
                        onChange({ hours });
                      } else {
                        updateAsset({ id, hours });
                      }

                      setIsEditing(false);
                    }}
                  />
                ) : (
                  <Text
                    noMargin
                    weight="semiBold"
                    color={
                      !!vehicle.endOfLifeHours &&
                      !isNaN(vehicle?.endOfLifeHours) &&
                      vehicle?.endOfLifeHours <= vehicle?.hours
                        ? 'red'
                        : 'primary'
                    }
                  >
                    {`${vehicle?.hours ?? 0}`}
                  </Text>
                )}
                <div className={styles.usageText}>
                  <Text noMargin color="secondary" weight="semiBold">
                    {t(keys.timecards.HOURS).toLowerCase()}
                  </Text>
                  {vehicle?.usageUpdateRequired ? (
                    <Text noMargin color="red" weight="semiBold">
                      *
                    </Text>
                  ) : null}
                </div>
              </div>
            ) : null}
          </div>
          {!readOnly && (
            <Icon
              onClick={() => {
                setIsEditing(!isEditing);
              }}
              hover
              style={{ opacity: '0.7', fontSize: '1.5rem' }}
            >
              {!!isEditing ? 'check' : 'edit'}
            </Icon>
          )}
        </div>
      </div>
    </div>
  );
};

const Header = ({
  vehicle,
  setIsEditing,
  isEditing,
  updateAsset,
  readOnly,
  state,
  t,
  onChange,
  size = 'md',
  navigate,
  workspaceId,
  disableDirectWrite,
  online,
  setState,
}) => {
  // eslint-disable-next-line
  const [prevValue, setPrevValue] = useState(
    vehicle?.odometer ?? vehicle?.hours,
  );
  const { id, hours, odometer } = state || {};

  return (
    <div className={styles.container}>
      <div className={styles.headerImageContainer}>
        <img
          src={vehicle?.imageUrl ?? noImage}
          onClick={() =>
            navigate(getRoute(workspaceId, paths.asset, vehicle?.id))
          }
          className={classNames([styles.image, styles[size]])}
          alt="vehicle"
        />
        <div>
          <Text
            hover={online}
            noMargin
            color="accentPrimary"
            onClick={() => {
              if (!online) return;
              navigate(getRoute(workspaceId, paths.asset, vehicle?.id));
            }}
          >
            {vehicleString(vehicle)}
          </Text>
          <Text size="sm" color="secondary" noMargin>
            {`${
              t(getTranslationKey(vehicle?.type, 'assets')) || vehicle?.type
            } - ${
              t(getTranslationKey(vehicle?.subtype, 'assets')) ||
              vehicle?.subtype
            }`}
          </Text>
        </div>
      </div>
      <div className={styles.details}>
        <div>
          {vehicle.showDistance ? (
            <div className={styles.row}>
              {isEditing && !readOnly ? (
                <Form.Control
                  className={
                    state?.usageUpdateRequired
                      ? styles.superHighlight
                      : styles.formHeader
                  }
                  type="number"
                  value={state?.odometer}
                  placeholder="Enter odometer"
                  size="lg"
                  min={0}
                  onChange={(e) =>
                    setState({ ...state, odometer: parseInt(e.target.value) })
                  }
                  onBlur={() => {
                    if (disableDirectWrite) {
                      onChange({ odometer });
                    } else {
                      updateAsset({ id, odometer });
                    }

                    setIsEditing(false);
                  }}
                />
              ) : (
                <Text noMargin weight="semiBold" size="md-lg">
                  {`${vehicle?.odometer ?? 0}`}
                </Text>
              )}
              <div className={styles.usageText}>
                <Text noMargin weight="semiBold" size="md-lg">
                  {vehicle?.isMetric ? 'km' : 'mi'}
                </Text>
                {vehicle?.usageUpdateRequired ? (
                  <Text noMargin color="red" weight="semiBold">
                    *
                  </Text>
                ) : null}
              </div>
            </div>
          ) : null}
          {vehicle.showHours ? (
            <div className={styles.row}>
              {isEditing && !readOnly ? (
                <Form.Control
                  type="number"
                  min={0}
                  size="lg"
                  className={
                    state?.usageUpdateRequired
                      ? styles.superHighlight
                      : styles.formHeader
                  }
                  value={state?.hours}
                  placeholder="Enter hours"
                  onChange={(e) =>
                    setState({ ...state, hours: parseFloat(e.target.value) })
                  }
                  onBlur={() => {
                    if (disableDirectWrite) {
                      onChange({ hours });
                    } else {
                      updateAsset({ id, hours });
                    }

                    setIsEditing(false);
                  }}
                />
              ) : (
                <Text
                  noMargin
                  weight="semiBold"
                  color={
                    !!vehicle.endOfLifeHours &&
                    !isNaN(vehicle?.endOfLifeHours) &&
                    vehicle?.endOfLifeHours <= vehicle?.hours
                      ? 'red'
                      : 'primary'
                  }
                  size="md-lg"
                >
                  {`${vehicle?.hours ?? 0}`}
                </Text>
              )}
              <div className={styles.usageText}>
                <Text noMargin weight="semiBold" size="md-lg">
                  {t(keys.timecards.HOURS).toLowerCase()}
                </Text>
                {vehicle?.usageUpdateRequired ? (
                  <Text noMargin color="red" weight="semiBold">
                    *
                  </Text>
                ) : null}
              </div>
            </div>
          ) : null}
        </div>
        {!readOnly && (
          <Button
            onClick={() => {
              setIsEditing(!isEditing);
            }}
            variant={isEditing ? 'success' : 'secondary'}
            value={
              isEditing
                ? t(keys.action.SAVE)
                : t(keys.action.EDIT_VARIABLE, { variable: null })
            }
            icon={isEditing ? 'save' : null}
            size="sm"
          />
        )}
      </div>
      {prevValue && !isEditing ? (
        <Text noMargin color="secondary">
          {t(keys.assessments.PREVIOUS_ENTRY, {
            value: prevValue,
            unit: vehicle?.showDistance
              ? vehicle?.isMetric
                ? 'km'
                : 'mi'
              : 'hours',
          })}
        </Text>
      ) : null}
    </div>
  );
};

export default function InspectionItemAsset({
  vehicle,
  readOnly,
  disableDirectWrite = false,
  size = 'sm',
  variant = 'question',
  onChange = () => {},
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { workspaceId } = useWorkspace();
  const [isEditing, setIsEditing] = useState(readOnly ? false : true);
  const [state, setState] = useState({ vehicle, odometer: null, hours: null });
  const { updateAsset } = useInspection();
  const online = useOnlineStatus();

  useEffect(() => setState(vehicle), [vehicle]);

  return vehicle?.id ? (
    variant === 'header' ? (
      Header({
        vehicle,
        state,
        setIsEditing,
        isEditing,
        updateAsset,
        readOnly,
        t,
        onChange,
        navigate,
        workspaceId,
        disableDirectWrite,
        online,
        setState,
      })
    ) : (
      Question({
        vehicle,
        setIsEditing,
        isEditing,
        updateAsset,
        readOnly,
        state,
        t,
        onChange,
        size,
        navigate,
        workspaceId,
        disableDirectWrite,
        online,
        setState,
      })
    )
  ) : size !== 'sm' ? null : (
    <Text className={styles.textResponse} noMargin>
      {t(keys.common.NONE)}
    </Text>
  );
}
